---
title: Privacidad
updated: 2026-10-02
---

## 1. Responsable del Tratamiento

Draughts Game («nosotros») es el responsable del tratamiento de los datos personales recogidos a través de esta plataforma. Contacto: [privacy@draughtsgame.com](mailto:privacy@draughtsgame.com)

## 2. Datos que Recopilamos

### 2.1 Datos de la Cuenta

- **Nombre de usuario** - elegido por ti, visible públicamente
- **Dirección de correo electrónico** - cifrada en reposo con AES-256-GCM, utilizada para la identificación de la cuenta, no visible públicamente
- **Contraseña** - almacenada como hash bcrypt (factor de coste 12), nunca en texto plano
- **Confirmación de edad** - registramos que confirmaste tener al menos 16 años al registrarte
- **Estado de verificación del correo** - si has confirmado tu dirección de correo, además de tokens temporales de verificación y de restablecimiento de contraseña
- **Personalización del perfil (opcional)** - avatar y país, elegidos por ti, visibles públicamente

### 2.2 Datos de Juego

- Movimientos, resultados y marcas de tiempo de las partidas
- Clasificaciones y estadísticas de los jugadores
- Participación en torneos y resultados
- Intentos de problemas, clasificación de problemas y rachas de resolución
- Tu biblioteca de estudio: notas de posición (texto libre que escribes tú), carpetas, partidas guardadas y posiciones guardadas en el editor — privadas, visibles solo para ti

### 2.3 Datos Técnicos

- Dirección IP (por seguridad y antitrampas)
- Cadena user-agent del navegador
- Tokens de sesión

### 2.4 Solicitudes de Soporte

Si nos contactas a través del formulario de soporte, recopilamos tu dirección de correo electrónico, un nombre opcional y el asunto y contenido de tu mensaje, para poder gestionar y responder tu solicitud.

## 3. Base Jurídica del Tratamiento

Tratamos tus datos personales sobre la base de:

- **Ejecución del contrato** (art. 6(1)(b) RGPD) - para prestar el servicio de juego al que te registraste, incluidos los correos transaccionales (verificación de cuenta, restablecimiento de contraseña) y las respuestas de soporte.
- **Intereses legítimos** (art. 6(1)(f) RGPD) - para la seguridad de la plataforma, antitrampas, estadísticas agregadas sobre el juego (qué variantes se juegan, cómo terminan las partidas) y mejora del servicio.
- **Consentimiento** (art. 6(1)(a) RGPD) - para la medición de uso mediante Umami y el seguimiento de errores mediante Sentry. Ninguno de los dos se activa hasta que aceptas en el aviso.

## 4. Cómo Utilizamos tus Datos

- Prestación y funcionamiento de la plataforma de juego
- Autenticación de tu cuenta y mantenimiento de sesiones
- Cálculo y visualización de clasificaciones y rankings
- Emparejamiento y organización de torneos
- Detección y prevención de trampas y abusos
- Envío de correos transaccionales (verificación de cuenta, restablecimiento de contraseña)
- Gestión y respuesta a tus solicitudes de soporte
- Mejora del servicio mediante estadísticas agregadas y anónimas
- Análisis de los datos de juego (p. ej. variantes jugadas, resultados) de forma agregada y anonimizada para comprender el uso y mejorar la plataforma

## 5. Cookies y Servicios de Terceros

Utilizamos cookies esenciales para la autenticación y tus preferencias. Nos apoyamos en los siguientes servicios:

- **Umami** (analítica) - autoalojado en nuestra propia infraestructura en la UE. No usa cookies y solo se activa si aceptas la analítica en el aviso. Las visitas se identifican mediante una huella seudonimizada, derivada de tu dirección IP y tu navegador y renovada cada día; las cifras se usan únicamente de forma agregada y no te rastreamos entre sitios.
- **Sentry** (seguimiento de errores) - activado solo con tu consentimiento.
- **Resend** (envío de correos) - entrega nuestros correos transaccionales (verificación de cuenta, restablecimiento de contraseña) y las notificaciones de soporte.
- **DigitalOcean** (alojamiento) - centro de datos en la UE (Fráncfort).

No vendemos tus datos ni utilizamos rastreadores publicitarios.

## 6. Conservación de los Datos

- **Datos de la cuenta** - se conservan mientras tu cuenta esté activa.
- **Cuentas de invitado** - se eliminan automáticamente tras 30 días de inactividad.
- **Cuentas no verificadas** - si te registras pero nunca confirmas tu dirección de correo electrónico, la cuenta se elimina automáticamente tras 30 días de inactividad.
- **Tokens de actualización** - caducan a los 7 días (90 días en las cuentas de invitado, para que puedas reencontrar tus partidas sin contraseña), se limpian automáticamente cada hora. La dirección IP y los datos del navegador asociados a una sesión se borran a los 7 días, aunque la sesión dure más.
- **Tokens de verificación de correo** - caducan a las 24 horas.
- **Tokens de restablecimiento de contraseña** - caducan en 1 hora.
- **Solicitudes de soporte** - se conservan el tiempo necesario para gestionar tu solicitud.
- **Denuncias de abuso** - cuando un jugador denuncia a alguien por abuso en el chat, el chat de esa partida (hasta 100 mensajes, de ambos jugadores) se conserva para su moderación y se elimina automáticamente a los 90 días. Por lo demás, el chat nunca se conserva.
- **Datos de juego** - se conservan indefinidamente como parte del registro público de partidas.
- **Biblioteca de estudio** - las notas de posición, las carpetas, las partidas guardadas y las posiciones guardadas se conservan hasta que las elimines, y en todo caso se eliminan al borrar tu cuenta.
- **Datos de errores de Sentry** - se eliminan automáticamente a los 90 días.
- **Cuentas eliminadas** - los datos personales se eliminan inmediatamente. Los registros de partidas se anonimizan (se elimina la referencia al jugador).

## 7. Tus Derechos (RGPD)

En virtud del Reglamento General de Protección de Datos (RGPD), tienes derecho a:

- **Acceso** (art. 15) - descarga una copia de tus datos personales desde la configuración de tu perfil («Exportar mis datos»).
- **Rectificación** (art. 16) - cambia tu contraseña en la configuración del perfil. Para otras correcciones, contáctanos.
- **Supresión** (art. 17) - elimina tu cuenta desde la configuración del perfil («Eliminar cuenta»). Es inmediato e irreversible.
- **Portabilidad** (art. 20) - exporta tus datos en formato JSON legible por máquina desde la configuración del perfil.
- **Limitación** (art. 18) - solicita la limitación del tratamiento contactándonos.
- **Oposición** (art. 21) - rechazar la analítica en el banner de cookies desactiva **tanto** la medición de uso de Umami **como** el seguimiento de errores de Sentry. Puedes cambiar de opinión en cualquier momento desde el interruptor en la configuración de tu perfil.

Para ejercer cualquiera de estos derechos, utiliza las herramientas de autoservicio en la configuración de tu perfil o contáctanos en [privacy@draughtsgame.com](mailto:privacy@draughtsgame.com). Responderemos en un plazo de 30 días.

## 8. Seguridad de los Datos

Aplicamos medidas técnicas y organizativas apropiadas para proteger tus datos personales, incluido el cifrado de datos sensibles en reposo y en tránsito, el hash seguro de contraseñas y la limitación de peticiones en los puntos de autenticación.

## 9. Transferencias Internacionales

Tus datos se almacenan y procesan principalmente dentro del Espacio Económico Europeo (EEE), en el centro de datos de DigitalOcean en Fráncfort. Los datos de errores de Sentry pueden transferirse a EE. UU. conforme a la decisión de adecuación del Marco de Privacidad de Datos UE-EE. UU. (julio de 2023). Puedes desactivar Sentry en la configuración de tu perfil. Los correos transaccionales se entregan a través de Resend (EE. UU.); esta transferencia está cubierta por un acuerdo de tratamiento de datos con Cláusulas Contractuales Tipo.

## 10. Privacidad de los Menores

El Servicio no está destinado a menores de 16 años (art. 8 RGPD). Exigimos confirmación de edad al registrarse. No recopilamos deliberadamente datos personales de menores de 16 años. Si descubrimos que hemos recopilado datos de un menor de 16 años, los eliminaremos de inmediato.

## 11. Cambios en esta Política

Podemos actualizar esta Política de Privacidad ocasionalmente. Notificaremos a los usuarios registrados los cambios importantes mediante un aviso en la plataforma. La fecha de «Última actualización» en la parte superior indica la revisión más reciente.

## 12. Autoridad de Control

Tienes derecho a presentar una reclamación ante la autoridad de control de protección de datos competente si consideras que se han vulnerado tus derechos.

## 13. Contacto

Para preguntas sobre esta Política de Privacidad:

- Correo electrónico: [privacy@draughtsgame.com](mailto:privacy@draughtsgame.com)
